/**
 * The one component that draws a card (C1).
 *
 * It lives in its own module rather than inside `Zone.tsx` so that "one
 * component owns card rendering" is true of the file tree and not only of the
 * intention. Everything that puts a card on screen — a hand, a pile, a trick, a
 * card in flight — comes through here, so art arrives once.
 *
 * ## What survives a skin
 *
 * The `aria-label` (C6). It is derived from the card id by `faceOf`, never from
 * the skin, and that is not an implementation detail: C3 lets a skin replace
 * all 54 faces, so under a themed deck the label is the *only* thing telling a
 * screen-reader user what they are holding. A skin that could change it could
 * make the deck unreadable, which is why the label is computed before any skin
 * is consulted and nothing in `Skin` can reach it.
 *
 * `data-card` likewise. The play animation measures the tapped card to know
 * where a flight starts, and the hand finds it by this attribute. It is load
 * bearing, not decoration.
 */

import { faceOf } from './cards.js';
import type { ArcPlacement } from './fan.js';
import { useSkin } from './skin-context.js';
import { faceImage } from './skin.js';
import { useCardMarks } from './marks-context.js';

export function Card({
  card,
  onClick,
  playable,
  selected,
  fresh,
  arriving,
  small,
  arc,
}: {
  card: string;
  /*
   * `| undefined` on every optional prop, because `exactOptionalPropertyTypes`
   * distinguishes "absent" from "present and undefined" and JSX spreads pass
   * the latter. Widening here beats threading conditional spreads through
   * every call site.
   */
  onClick?: (() => void) | undefined;
  playable?: boolean | undefined;
  selected?: boolean | undefined;
  /** Just arrived in this hand, so it can be found without reading the row. */
  fresh?: boolean | undefined;
  /**
   * Still in the air: it holds its place in the fan but is not drawn.
   *
   * Hidden rather than left out, so the row is already the width it will be
   * and nothing shuffles sideways when the card finally lands.
   */
  arriving?: boolean | undefined;
  small?: boolean | undefined;
  /**
   * Where this card sits on its row's arc, for a hand (H2).
   *
   * Applied as custom properties rather than as a transform, because four
   * other rules already move a card - it sits back when unplayable, lifts when
   * playable, lifts further while pressed, and further still when selected.
   * A transform written here would replace theirs rather than combine with
   * them, so the stylesheet composes one transform out of both contributions.
   */
  arc?: ArcPlacement | undefined;
}) {
  const skin = useSkin();
  // Computed from the card id, before the skin is looked at. See the note above.
  const face = faceOf(card);

  /*
   * Somebody took a crayon to this rank.
   *
   * Declared by the game - see `cardMarks` in the toolkit - so the renderer
   * never decides that a two means anything. A game that has written on no
   * cards hands back an empty map and every card below is an ordinary one.
   */
  const mark = useCardMarks()[face.rank];

  const classes = ['card'];
  if (mark !== undefined) classes.push('marked');
  if (face.red) classes.push('red');
  if (playable) classes.push('playable');
  if (selected) classes.push('selected');
  if (fresh) classes.push('fresh');
  if (arriving) classes.push('arriving');
  if (small) classes.push('small');

  const style = arc
    ? ({ '--angle': `${String(arc.angle)}deg`, '--drop': String(arc.drop) } as React.CSSProperties)
    : undefined;

  /*
   * Classic draws the face from text; a sprite paints it.
   *
   * Both keep the same element, the same classes and the same label - a skin
   * changes what a card looks like and never what it is.
   */
  /* One image per card, for a painted deck. The label and `data-card` above
     are untouched: a skin changes what a card looks like, never what it is. */
  const painted = faceImage(skin.faces, card);

  const body = painted !== null ? (
    <img
      className="card-image"
      src={painted}
      alt=""
      /* Decorative: `aria-label` on the card itself already names it, and a
         second name here would have a screen reader say it twice. */
      aria-hidden="true"
      draggable={false}
      /*
       * Eager, deliberately.
       *
       * A hand of thirteen is thirteen requests that all matter at once, and a
       * lazily-loaded card pops in after the deal - which reads as the app
       * being slow rather than as an image arriving. They are 8KB each.
       */
      loading="eager"
      /*
       * Decoded off the main thread.
       *
       * A hand of thirteen decodes thirteen pictures, and doing that
       * synchronously blocks the frame that was about to draw them - which on
       * a phone is the difference between a deal and a stutter. The order they
       * appear in is not something a player can perceive; the frame dropping
       * is.
       */
      decoding="async"
    />
  ) : skin.faces.kind === 'sprite' ? (
      <span
        className="card-art"
        style={{ backgroundImage: `url("${skin.faces.src}")` }}
        data-face={`${face.rank}${face.suit}`}
      />
    ) : (
      <>
        {/*
          The corner index, which is the only part of a card you can rely on
          seeing.

          A card in a column or a fan shows one strip of itself and the rest is
          under the next card. With the rank and suit centred, that strip held
          11px of a 28px rank, cut across the middle, and no suit at all - so an
          overlapped card could not be identified, and in Klondike, whose rule
          is alternating colour, the colour was the part that went missing.

          This is why real cards have carried a corner index for two hundred
          years. The big centred pair stays for a card you can see all of.

          `aria-hidden`, because `aria-label` on the card already gives the
          whole name and a screen reader should not hear it twice.
        */}
        <span className="card-index" aria-hidden="true">
          <span className="card-index-rank">{face.rank}</span>
          <span className="card-index-suit">{face.suit}</span>
        </span>
        <span className="rank">{face.rank}</span>
        <span className="suit">{face.suit}</span>

        {/*
          The crayon, in two places, because a card is read at two sizes.

          In a hand you see the left edge and nothing else - a thirteen-card
          fan steps 22px, of which the index takes x4 to x13 - so the strike
          through the index is the whole signal there: SOMETHING has been done
          to this card. The word across the face is for the discard pile, the
          card in flight, and any card you are actually looking at.

          `aria-hidden` on both. The card's `aria-label` is its name and a
          screen reader should hear "two of hearts", not a description of the
          vandalism; what the two DOES is the game's rules, which belong in
          the objective and the legend rather than on every card.
        */}
        {mark === undefined ? null : (
          <>
            <span className="card-strike" aria-hidden="true" />
            <span className="card-mark" aria-hidden="true">
              {mark}
            </span>
          </>
        )}
      </>
    );

  // A non-playable card is not a button: it should not take focus, and a screen
  // reader should not offer it as an action.
  if (!onClick) {
    return (
      <div className={classes.join(' ')} style={style} aria-label={face.label} role="img">
        {body}
      </div>
    );
  }

  return (
    // `data-card` names it so the hand can measure where a tapped card was
    // sitting and fly a copy of it to wherever the move sends it.
    <button
      type="button"
      className={classes.join(' ')}
      style={style}
      data-card={card}
      onClick={onClick}
      aria-label={face.label}
    >
      {body}
    </button>
  );
}

/**
 * The back of a card.
 *
 * Painted from `--card-back`, which the equipped skin sets. The stylesheet used
 * to hardcode the gradient here *and* again on the flying card, with a comment
 * on the second one claiming it was "literally the back `.card.back` draws".
 * It was, by hand - and would have stopped being so the first time a skin
 * changed one of them, silently, with nothing to fail.
 *
 * `aria-hidden` because a face-down card has nothing to tell anybody; the count
 * beside a fan of them is what carries the information.
 */
export function CardBack({ small }: { small?: boolean | undefined }) {
  return <div className={`card back${small ? ' small' : ''}`} aria-hidden="true" />;
}
